import { usePortfolioGroupVersionStatData } from "../../usePortfolioGroupVersionStatData";
import { usePortfolioGroupParams } from "@app/hooks/usePortfolioGroupParams";
import { usePortfolioGroupInfo } from "../../usePortfolioGroupInfo";
import { TablePlaceHolder } from "@app/components/PlaceHolder";
import { CarPageContentCode, usePageContent } from "@app/hooks/usePageContent";
import { CarPageContent } from "@app/components/PageContent";
import { ChartModuleSummary } from "./../ChartModuleSummary";
import {
  usePortfolioGroupVersionData,
  UsePortfolioGroupVersionDataParams,
} from "../../usePortfolioGroupVersionData";
import { AssetClassLevel } from "@app/const";
import { CarAssetClassLevelField } from "@app/components/Inputs";
import { useSessionStorage } from "@app/hooks/useLocalStorage";
import { AnalyzeModuleStyledPage } from "./AnalyzeModuleStyledPage";
import { CarTooltipBox } from "@app/components/TooltipBox";
import { pendoClasses } from "@app/core/thirdParty/pendo";
import { useAnalyzeModuleChartDataSelector } from "./useAnalyzeModuleChartDataSelector";
import { CarDownloadButton } from "@app/components/Buttons";
import { openInNewTab } from "@app/utils";
import {
  assetAllocationPages,
  portfolioAnalysisPages,
} from "@app/const/route.const";

interface AnalyzeModuleSummaryProps {
  moduleId: string;
  versionParams: UsePortfolioGroupVersionDataParams;
  assetClassLevelStorageKey: string;
}

export const AnalyzeModuleSummary = (props: AnalyzeModuleSummaryProps) => {
  const { portfolioGroupId } = usePortfolioGroupParams();

  const pageContent = usePageContent();

  const { moduleId } = props;

  const groupInfo = usePortfolioGroupInfo({ portfolioGroupId });

  const data = usePortfolioGroupVersionData(props.versionParams);

  const statData = usePortfolioGroupVersionStatData(props.versionParams);

  const defaultIds = statData.getDefaultDataIdsForModule(moduleId);

  const chartDataSelector = useAnalyzeModuleChartDataSelector({
    portfolioGroupVersionId: props.versionParams.versionId,
    ...defaultIds,
  });

  const [assetClassLevel, setAssetClassLevel] =
    useSessionStorage<AssetClassLevel>(
      props.assetClassLevelStorageKey,
      AssetClassLevel.level1,
    );

  const handleShowPdf = () => {
    if (chartDataSelector.data1Id && chartDataSelector.data2Id) {
      const params = {
        portfolioGroupId,
        moduleId: props.moduleId,
        data1Id: chartDataSelector.data1Id,
        data2Id: chartDataSelector.data2Id,
      };
      openInNewTab(
        groupInfo.isSinglePortfolioMode
          ? portfolioAnalysisPages.getPdfSummaryReportUrl(params)
          : assetAllocationPages.getPdfModuleSummaryReportUrl(params),
      );
    }
  };

  return (
    <AnalyzeModuleStyledPage>
      <TablePlaceHolder
        isLoading={
          groupInfo.isLoading || data.isLoading || pageContent.isLoading
        }
        rows={5}
      >
        <CarPageContent
          isWhiteContext
          content={pageContent.getContent(
            groupInfo.isSinglePortfolioMode
              ? CarPageContentCode.PORTFOLIO_ANALYSIS_ANALYZE_SUMMARY
              : CarPageContentCode.ANALYZE_PORTFOLIO_GROUP_REPORTS_MODULE_SUMMARY,
          )}
        />
        <CarDownloadButton
          onClick={handleShowPdf}
          sx={{ mt: 2, zIndex: 1, ml: "auto", alignSelf: "flex-end" }}
        >
          Download PDF
        </CarDownloadButton>

        <CarAssetClassLevelField
          sx={{
            width: 150,
            ".MuiInputLabel-root": {
              fontSize: "16px",
            },
            ".MuiOutlinedInput-root": {
              mt: 3,
            },
            ".MuiInputBase-input": {
              backgroundColor: "white",
            },
          }}
          label={
            <>
              Asset Class Level
              <CarTooltipBox
                sx={{ display: "inline-block", ml: 0.5, mb: -0.5 }}
                className={pendoClasses.editPortfolioAnalyzeAssetClassLevel}
              />
            </>
          }
          value={assetClassLevel}
          onChange={setAssetClassLevel}
        />
        <ChartModuleSummary
          moduleId={props.moduleId}
          tableData={data.tableData}
          level={assetClassLevel}
          chartDataSelector={chartDataSelector}
        />
      </TablePlaceHolder>
    </AnalyzeModuleStyledPage>
  );
};
